import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";

import ArtifactClient from "../backend/clients/ArtifactClient";
import { useToolContext } from "../domain/ToolContext/ToolContext";
import { Artifact, Version } from "../models";
import AggregatedLogger from "../observability/AggregatedLogger/AggregatedLogger";
import normalizeError from "../utils/errors";

export default function useVersionData(artifact: Artifact) {
  const context = useToolContext();
  const pageSize = useMemo(() => 50, []);
  const [data, setData] = useState<Version[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const getVersions = useCallback(async () => {
    setData(null);
    setError(null);
    setLoading(true);
    try {
      let continuationToken = "";
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await ArtifactClient.getVersions(
          context.token,
          artifact.placeId,
          artifact.id,
          continuationToken,
          pageSize,
        );

        continuationToken = response.continuationToken || "";

        if (!response.items) {
          AggregatedLogger.error("artifact client: get versions: null items");
          setError(
            normalizeError(error, new Error(t("api_error_msg.version_err"))),
          );
        } else {
          setData((data) => [...(data ?? []), ...(response.items ?? [])]);
        }
      } while (continuationToken);
    } catch (error) {
      AggregatedLogger.error("artifact client: get versions: ", error);
      setError(normalizeError(error, new Error("Failed to get versions.")));
    }
    setLoading(false);
  }, [data, artifact.id, artifact.placeId, context, pageSize]);

  return {
    getVersions,
    data,
    error,
    loading,
  };
}
